import axios from "axios";
import Dexie from "dexie";

import PronUtils from "./pronUtils";

const levenshtein = require("fast-levenshtein");
const diff = require("diff");

const DICT_URL =
  "https://firebasestorage.googleapis.com/v0/b/pronaid-beta.appspot.com/o/cmudictSymbol.json?alt=media&token=5de3891a-7686-4bb6-ac9b-ee1b1029e5bb";

var db = new Dexie("PronAidDB");
db.version(1).stores({
  dict: "dictName, data, version"
});

class PronDictionary {
  dictionaryLoaded = false;

  constructor() {
    // const N = 200
    // const a = Array.apply(null, {length: N}).map(Function.call, Number)
    // console.log(a.map((e)=> {
    //     return String.fromCharCode(parseInt(e))
    // }))
    const self = this;
    db.dict
      .get("cmudict2", function(dictData) {
        const currentVer = dictData.data.version;
        const currentDict = dictData.data;

        if (Object.keys(currentDict).length < 125000) {
          // if (true) {
          self.installDictionary();
        } else {
          console.log("dictionary found");
          self.dict = currentDict;
        }
      })
      .catch(err => {
        console.log("could not get dict data");
        self.installDictionary();
      });
  }

  getDictInstalled() {
    return this.dictInstalled;
  }

  // should return promise and do error handlings and add UI in dashbord
  installDictionary(currentVersion, previousVersion) {
    console.log("installing");
    let self = this;
    axios
      .get(DICT_URL)
      .then(data => {
        const dictData = data.data;
        const version = 2;
        self.dict = dictData;
        db.dict.put({
          dictName: "cmudict2",
          version: version,
          data: dictData
        });
      })
      .then(() => {
        console.log("install success");
      });
  }

  _stripDiff(diff) {
    const diffs = [];
    diff.forEach(el => {
      const isRemoved = el.removed;
      const isAdded = el.added;
      el.value.split("").forEach(ph => diffs.push({ ph, isRemoved, isAdded }));
    });
    return diffs;
  }

  _createPronOutput(filteredPron, accentPos) {
    return filteredPron.map((el, index) => {
      return {
        phoneme: PronUtils.toIpa(el, accentPos[index]),
        phonemeWithoutStress: PronUtils.toIpaWithoutStress(el),
        basePhoneme: el,
        isSpace: el.ph === " ",
        isAdded: el.isAdded,
        isRemoved: el.isRemoved,
        isPrimary: accentPos[index].isPrimary,
        isSecondary: accentPos[index].isSecondary
      };
    });
  }

  _calcAccentPosition(pronSymbols) {
    const accentPos = pronSymbols.split("").map(el => {
      if (el !== undefined) {
        return {
          phoneme: el,
          isPrimary: PronUtils.isPrimaryAccent(el),
          isSecondary: PronUtils.isSecondaryAccent(el)
        };
      }
    });
    return accentPos;
  }

  getSymbolFromText(originalText) {
    return new Promise(resolve => {
      const cleanedText = this.cleanText(originalText);
      const pron = cleanedText
        .split(" ")
        .map(ph => {
          return this.dict[ph] ? this.dict[ph] : "";
        })
        .join(" ");
      resolve(pron);
    });
  }

  // -> split by space and do this every word in a sentence
  cleanText(str) {
    let text = str
      .toLowerCase()
      .trim()
      .replace("’", "'")
      .replace(",", "");
    if (text.endsWith(".") || text.endsWith("?") || text.endsWith("!")) {
      text = text.slice(0, -1);
    }
    return text;
  }

  getScore(modelText, userText) {
    return new Promise(resolve => {
      let _modelText = this.cleanText(modelText);
      let _userText = this.cleanText(userText);

      const promises = [
        this.getSymbolFromText(_modelText),
        this.getSymbolFromText(_userText)
      ];

      Promise.all(promises).then(result => {
        const score =
          100 -
          (levenshtein.get(result[0], result[1]) /
            Math.max(result[0].length, result[1].length)) *
            100;
        resolve({
          score,
          modelText
        });
        if (!score) {
          reject(new Error("No score was calculated."));
        }
      });
    });
  }

  getIpaFromSingleText(originalText) {
    return new Promise(resolve => {
      let cleaned = this.cleanText(originalText);
      const pron = cleaned.split(" ").map(word => {
        return this.dict[word] || null;
      });
      const ipa = pron.map(s => {
        return PronUtils.symbolsToIpa(s);
      });
      resolve(ipa);
    });
  }

  getIpaSetFromSingleText(originalText) {
    return new Promise(resolve => {
      const pron = originalText.split(" ").map(word => {
        return this.dict[word] || null;
      });
      const ipa = pron.map(s => {
        return PronUtils.symbolsToIpa(s);
      });
      resolve(ipa);
    });
  }

  compareSymbolFromTexts(modelText, userText) {
    return new Promise((resolve, reject) => {
      let _modelText = this.cleanText(modelText);
      let _userText = this.cleanText(userText);

      const getIpaPromises = [
        this.getSymbolFromText(_modelText),
        this.getSymbolFromText(_userText)
      ];

      Promise.all(getIpaPromises).then(pronResult => {
        const pronResultModel = pronResult[0];
        const pronResultUser = pronResult[1];

        const modelAccentPos = this._calcAccentPosition(pronResultModel);
        const userAccentPos = this._calcAccentPosition(pronResultUser);

        const accentIgnoredModel = PronUtils.toAccentIgnored(pronResultModel);
        const accentIgnoredUser = PronUtils.toAccentIgnored(pronResultUser);

        const diffs = diff.diffChars(accentIgnoredModel, accentIgnoredUser);
        let modelFilteredDiff = diffs.filter(el => {
          return !el.added;
        });

        let userFilteredDiff = diffs.filter(el => {
          return !el.removed;
        });

        const score =
          100 -
          (levenshtein.get(accentIgnoredModel, accentIgnoredUser) /
            Math.max(accentIgnoredModel.length, accentIgnoredUser.length)) *
            100;

        modelFilteredDiff = this._stripDiff(modelFilteredDiff);

        userFilteredDiff = this._stripDiff(userFilteredDiff);

        const modelOutput = this._createPronOutput(
          modelFilteredDiff,
          modelAccentPos
        );
        const userOutput = this._createPronOutput(
          userFilteredDiff,
          userAccentPos
        );

        const result = { modelOutput, userOutput, score };

        resolve(result);
        if (!result) {
          reject(new Error("Diffs were not created."));
        }
      });
    });
  }
}

const pronDict = new PronDictionary();

export default pronDict;
